import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { updateError } from './user';
import { dispatch } from '../store';
import { isConnectedWhatsappBusinessAccount } from '../../utils/whatsappAccountLifecycle';
import {
    UNCONFIRMED_SEND_MESSAGE,
    isUnconfirmedSendOutcome,
    newClientMessageId,
    shouldRecheckSend,
} from '../../utils/whatsappSendSafety';

// Maps a non-OK callApi response to a friendly user-facing message and a
// toast severity. Returns null for responses that should be silent (401 is
// handled by the auth layer; 403 is kept silent because not every practice
// has WhatsApp configured and we don't want to alarm those users). Non-5xx
// friendly messages use `'warning'` so they do NOT capture to Sentry — 404s
// and 400s on stale account/phone IDs are expected user states, not bugs.
// 5xx keeps `success: false` so real backend failures still capture.
const getFriendlyWhatsappError = (response) => {
    if (response?.status === 401) return null;
    if (response?.status === 403) return null;
    if (response?.status === 404) {
        return { message: 'WhatsApp account not found. Please refresh and try again.', severity: 'warning' };
    }
    if (response?.status >= 500) {
        // Reached only by the account/phones READ callers, for which "nothing
        // happened" is the truth. The SEND path must never use this wording — a
        // 5xx there can mean the patient already has the message — so
        // whatsappMessageSend intercepts its own 5xx above this call.
        return { message: 'WhatsApp service is temporarily unavailable.', severity: false };
    }
    return {
        message: response?.data?.error || response?.data?.message || "Couldn't load WhatsApp account.",
        severity: 'warning',
    };
};

const initialState = {
    isLoading: false,
    whatsappAccountData: null,
    whatsappAccountPhonesData: null
};

const slice = createSlice({
    name: 'whatsappAccount',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        endLoading(state) {
            state.isLoading = false;
        },
        getAccountList(state, action) {
            state.isLoading = false;
            state.whatsappAccountData = action.payload;
        },
        getAccountPhonesList(state, action) {
            state.isLoading = false;
            state.whatsappAccountPhonesData = action.payload;
        },
    }
});

export default slice.reducer;

export const { clearMessage, reset, updateSubjectTemplateList, updateShowSubjectList } = slice.actions;

export function getWhatsappAccount(id) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${id}/whatsapp-business-accounts/`);
            if (response.status === 200) {
                dispatch(slice.actions.getAccountList(response.data));
                dispatch(slice.actions.endLoading());
            } else {
                const friendly = getFriendlyWhatsappError(response);
                if (friendly) {
                    dispatch(updateError({ message: friendly.message, success: friendly.severity }));
                }
                dispatch(slice.actions.endLoading());
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Network error', success: false }));
        } finally {
            dispatch(slice.actions.endLoading());
        }
    };
}

export function getWhatsappAccountPhones({ practiceId, id, handleSuccess }) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) { handleSuccess?.(); return; }
        // Guard: skip API call if business account PK is missing
        if (!practiceId || !id) { handleSuccess?.(); return; }
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/whatsapp-business-accounts/${id}/whatsapp-phones/`);
            if (response.status === 200) {
                dispatch(slice.actions.getAccountPhonesList(response.data));
                handleSuccess({ success: true, phoneId: response.data.results });
                return;
            }
            const friendly = getFriendlyWhatsappError(response);
            if (friendly) {
                dispatch(updateError({ message: friendly.message, success: friendly.severity }));
            }
            handleSuccess();
        } catch (error) {
            dispatch(updateError({ message: error?.message, success: false }));
        }
    };
}

export function whatsappMessageSend({
    practiceId,
    id,
    phoneId,
    messageData,
    handleSuccess,
    leadInfo,
    isBusinessAccountConnected,
}) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging') || !isConnectedWhatsappBusinessAccount({
            is_connected: isBusinessAccountConnected,
        })) { handleSuccess?.(); return; }
        try {
            const data = { ...messageData };
            let context = {};
            const respContextPractice = await callApi(
                `/api/v1/practices/${practiceId}/get_context/`,
                'GET'
            );
            const respContextLead = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadInfo?.id}/get_context/`,
                'GET'
            );
            const responseTemplateForLead = await callApi(`/api/v1/practices/${practiceId}/leads/${leadInfo?.id}/treatments/`);
            const templateIdList = responseTemplateForLead?.data?.results?.map(dt => dt?.id) ?? [];
            let treatmentContext = {};
            // eslint-disable-next-line no-restricted-syntax
            for (const treatmentId of templateIdList) {
                // eslint-disable-next-line no-await-in-loop
                const treatMentContextResponse = await callApi(`/api/v1/practices/${practiceId}/leads/${leadInfo?.id}/treatments/${treatmentId}/get_context/`);
                treatmentContext = { ...treatmentContext, ...treatMentContextResponse?.data };
            }
            context = {
                ...context, ...(respContextPractice?.data ?? {}), ...(respContextLead?.data ?? {}), ...(treatmentContext ?? {})
            };
            data.template_context = context;
            data.lead_id = leadInfo?.id;
            // One idempotency key per Send click. Without it this surface had NO
            // duplicate protection at all: the backend's 60s replay cache is
            // keyed on `client_message_id`, so a double-submit or a retry after
            // an unconfirmed outcome was a second real message to the patient.
            data.client_message_id = newClientMessageId();

            const sendUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${id}/whatsapp-phones/${phoneId}/send_message/`;
            // `suppressServerErrorDispatch` — apiCall fires its own generic
            // "Something went wrong! (<endpoint>)" toast on any 5xx, which
            // stacked on top of the message below.
            const sendOptions = { suppressServerErrorDispatch: true };
            let response = await callApi(sendUrl, 'post', data, false, sendOptions);

            // A 5xx is an UNKNOWN outcome, not a failure: `_send_message` hands
            // the message to Meta before the request finishes. Re-POST once with
            // the SAME `client_message_id` — the backend replays the original
            // message instead of calling Meta again, so this resolves the
            // question with no risk of a duplicate. Not done for a timeout,
            // where the first request may still be in flight server-side and the
            // idempotency slot still reads PENDING.
            if (shouldRecheckSend(response)) {
                response = await callApi(sendUrl, 'post', data, false, sendOptions);
            }

            if (response?.status === 200) {
                // Covers a normal send AND an `idempotent_replay: true` response
                // (the first attempt had already reached Meta) — both mean the
                // patient has the message exactly once.
                handleSuccess({ success: true });
                return;
            }
            if (isUnconfirmedSendOutcome(response)) {
                dispatch(updateError({
                    message: UNCONFIRMED_SEND_MESSAGE,
                    success: false,
                    timeout: 8000,
                }));
                handleSuccess();
                return;
            }
            const friendly = getFriendlyWhatsappError(response);
            if (friendly) {
                dispatch(updateError({ message: friendly.message, success: friendly.severity }));
            }
            handleSuccess();
        } catch (error) {
            handleSuccess();
            dispatch(updateError({ message: error?.message || 'Network error', success: false }));
        }
    };
}
